import { LanguageSelector } from "@tellescope/ui";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@tellescope/i18n";

export interface FormWizardLanguageSelectorProps {
  /** English plus every language this form has a translation configured for. */
  languages: string[];
}

const isSupportedLanguage = (language: string): language is SupportedLanguage =>
  SUPPORTED_LANGUAGES.some((supported) => supported === language);

/**
 * Lets the enduser read the form in one of its configured languages.
 *
 * Changing the selection changes the app's i18n language, which is the single source of truth here —
 * the form has no language state of its own, so the chrome and the form's own text move together.
 *
 * Renders nothing when English is the only option, leaving a form without configured translations
 * exactly as it was.
 */
export const FormWizardLanguageSelector = ({ languages }: FormWizardLanguageSelectorProps) => {
  // A configured language the app has no locale for would render an unlabelled option, so it is left
  // out of the control even though its translations would resolve.
  const selectable = languages.reduce<SupportedLanguage[]>((acc, language) => {
    if (isSupportedLanguage(language)) acc.push(language);
    return acc;
  }, []);

  if (selectable.length < 2) return null;

  return (
    <div className="flex justify-end px-4 pt-2">
      <LanguageSelector size="sm" locales={selectable} />
    </div>
  );
};
